import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Edit3, Trash2, Activity, FileSpreadsheet, Calendar, Archive, Upload, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Save, X, Info, ShieldCheck, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import ImportModal from '../components/ImportModal';
import MovimentacaoValidationModal from '../components/MovimentacaoValidationModal';
import GroupPanel from '../components/GroupPanel';
import IconButton from '../components/IconButton';
import { groupData } from '../utils/grouping';

const Movimentacao = () => {
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [faixas, setFaixas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMov, setEditingMov] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingMov, setDeletingMov] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConsolidarModal, setShowConsolidarModal] = useState(false);
  const [consolidarForm, setConsolidarForm] = useState({ data: '', nome: '', mesano: '' });
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressState, setProgressState] = useState({ percent: 0, step: 0, status: 'idle', count: 0, errorMsg: '' });
  const progressRef = useRef(null);
  const [requestInfo, setRequestInfo] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [breakFields, setBreakFields] = useState([]);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'empresa', direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Configurações de colunas para o agrupamento
  const availableColumns = [
    { value: 'empresa', label: 'Empresa' },
    { value: 'grupo', label: 'Grupo' },
    { value: 'faixa', label: 'Faixa' },
    { value: 'sub', label: 'Sub' },
    { value: 'matricula', label: 'Matrícula' },
    { value: 'nasc', label: 'Data Nasc.' },
    { value: 'cpf', label: 'CPF' },
    { value: 'nome', label: 'Nome' },
    { value: 'id', label: 'ID' }
  ];

  const labelForField = (field) => availableColumns.find(o => o.value === field)?.label || field;

  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const handleAddGroup = (field) => {
    setBreakFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
  };

  const handleRemoveGroup = (field) => {
    setBreakFields((prev) => prev.filter((f) => f !== field));
  };

  const handleClearGroups = () => {
    setBreakFields([]);
  };

  const handleReorderGroup = (fromIndex, toIndex) => {
    setBreakFields((prev) => {
      const arr = [...prev];
      if (fromIndex < 0 || fromIndex >= arr.length) return arr;
      const [moved] = arr.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, arr.length));
      arr.splice(insertAt, 0, moved);
      return arr;
    });
  };

  const formatDateDisplay = (isoString) => {
    if (!isoString) return '-';
    if (isoString.includes('T')) {
        return new Date(isoString).toLocaleDateString('pt-BR');
    }
    const [yyyy, mm, dd] = isoString.split('-');
    return `${dd}/${mm}/${yyyy}`;
  };
  
  const isoToDisplay = (iso) => {
    if (!iso) return '';
    const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(String(iso));
    if (!m) return '';
    const yyyy = m[1], mm = m[2], dd = m[3];
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    const cleaned = String(cpf).replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const resolveClienteFromMov = (mov) => {
    if (!mov) return null;
    const byId = clientes.find(c => String(c.id_cli) === String(mov.id_cli));
    if (byId) return byId;
    const byCode = clientes.find(c => String(c.cd_supera_cli) === String(mov.cd_empresa_mov));
    if (byCode) return byCode;
    const byEmpresaMovAsId = clientes.find(c => String(c.id_cli) === String(mov.empresa_mov));
    if (byEmpresaMovAsId) return byEmpresaMovAsId;
    return null;
  };

  const getEmpresaNome = (mov) => {
    if (!mov) return '-';
    if (mov.cliente && mov.cliente.nome_cli) return mov.cliente.nome_cli;
    const cli = resolveClienteFromMov(mov);
    if (cli && cli.nome_cli) return cli.nome_cli;
    return mov.empresa_mov || '-';
  };

  const getGrupoDesc = (mov) => {
    if (!mov) return '-';
    if (mov.desc_grupo_mov) return mov.desc_grupo_mov;
    const g = grupos.find(g => String(g.id_grupo) === String(mov.id_grupo_mov));
    return g?.desc_grupo || '-';
  };

  const filteredMovs = React.useMemo(() => {
    const normalize = (text) => String(text || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    // Filtro de status: quando o botão "Ativo" está acionado, exibe somente ativos
    const base = showOnlyActive ? movimentacoes.filter(m => m.id_ativo === 1) : movimentacoes;

    const term = searchTerm.trim();
    if (!term) return base;

    let nameFilter = '';
    let cpfFilter = '';
    let empFilter = '';
    let generalFilter = term;

    // Extract N: (Nome)
     const nMatch = generalFilter.match(/N:\s*([^\s]+)/i);
     if (nMatch) {
       nameFilter = normalize(nMatch[1]);
       generalFilter = generalFilter.replace(nMatch[0], '');
     }
 
     // Extract C: (CPF)
     const cMatch = generalFilter.match(/C:\s*([^\s]+)/i);
     if (cMatch) {
       cpfFilter = normalize(cMatch[1]);
       generalFilter = generalFilter.replace(cMatch[0], '');
     }
 
     // Extract E: (Empresa)
     const eMatch = generalFilter.match(/E:\s*([^\s]+)/i);
     if (eMatch) {
       empFilter = normalize(eMatch[1]);
       generalFilter = generalFilter.replace(eMatch[0], '');
     }

    generalFilter = normalize(generalFilter.trim());

    return base.filter(m => {
      // Check specific filters if they exist
      if (nameFilter && !normalize(m.nome_mov).includes(nameFilter)) return false;
      if (cpfFilter && !normalize(m.cpf_mov).includes(cpfFilter)) return false;
      if (empFilter) {
         const empName = normalize(getEmpresaNome(m));
         if (!empName.startsWith(empFilter)) return false;
      }

      // Check general filter (remaining text) against all fields
      if (generalFilter) {
         const matchesGeneral = (
           normalize(m.nome_mov).includes(generalFilter) ||
           normalize(m.cpf_mov).includes(generalFilter) ||
           normalize(m.empresa_mov).includes(generalFilter) ||
           (m.cliente && normalize(m.cliente.nome_cli).includes(generalFilter))
         );
         if (!matchesGeneral) return false;
      }

      return true;
    });
  }, [movimentacoes, searchTerm, clientes, showOnlyActive]);

  const sortedMovs = React.useMemo(() => {
    const sorted = [...filteredMovs];
    sorted.sort((a, b) => {
      const getValue = (item, key) => {
        switch (key) {
          case 'empresa': return getEmpresaNome(item);
          case 'grupo': return getGrupoDesc(item);
          case 'faixa': return (faixas.find(f => f.id === item.id_faixa_mov) || {}).desc_faixa || item.id_faixa_mov || '';
          case 'sub': return item.id_sub_mov ?? '';
          case 'matricula': return item.matricula_mov || '';
          case 'nasc': return item.dt_nasc_mov || '';
          case 'cpf': return item.cpf_mov || '';
          case 'nome': return item.nome_mov || '';
          case 'id': return item.id_mov;
          case 'status': return item.id_ativo;
          case 'cdl_ace': return item.id_cdl_ace || '';
          default: return item[key];
        }
      };

      const valA = getValue(a, sortConfig.key);
      const valB = getValue(b, sortConfig.key);
      
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Tie-breakers (Empresa -> Nome)
      if (sortConfig.key !== 'empresa') {
         const empA = getEmpresaNome(a);
         const empB = getEmpresaNome(b);
         if (empA < empB) return -1;
         if (empA > empB) return 1;
      }
      
      if (sortConfig.key !== 'nome') {
         const nomeA = a.nome_mov || '';
         const nomeB = b.nome_mov || '';
         if (nomeA < nomeB) return -1;
         if (nomeA > nomeB) return 1;
      }

      return 0;
    });
    return sorted;
  }, [filteredMovs, sortConfig, faixas, grupos, clientes]);

  const getValue = (row, field) => {
    switch (field) {
      case 'empresa': return getEmpresaNome(row);
      case 'grupo': return getGrupoDesc(row);
      case 'faixa': 
        return (faixas.find(f => f.id === row.id_faixa_mov) || {}).desc_faixa || row.id_faixa_mov || '-';
      case 'sub': return row.id_sub_mov ?? '-';
      case 'matricula': return row.matricula_mov || '-';
      case 'nasc': return formatDateDisplay(row.dt_nasc_mov);
      case 'cpf': return formatCPF(row.cpf_mov);
      case 'nome': return row.nome_mov || '-';
      case 'id': return row.id_mov;
      default: return '-';
    }
  };

  const groupedData = React.useMemo(() => {
    return groupData(sortedMovs, breakFields, getValue);
  }, [sortedMovs, breakFields, faixas, grupos, clientes]);

  // Effect to expand all groups by default when grouping structure changes
  useEffect(() => {
    if (breakFields.length === 0) return;

    const collectKeys = (nodes, parentKey = '') => {
      let keys = [];
      nodes.forEach(node => {
        if (node.isGroup) {
          const uniqueKey = parentKey ? `${parentKey}-${node.groupValue}` : node.groupValue;
          keys.push(uniqueKey);
          if (node.children) {
            keys = keys.concat(collectKeys(node.children, uniqueKey));
          }
        }
      });
      return keys;
    };

    const allKeys = new Set(collectKeys(groupedData));
    setExpandedGroups(allKeys);
  }, [groupedData]);

  const importFields = [
    { key: 'nome_mov', label: 'Nome', required: true },
    { key: 'cpf_mov', label: 'CPF', required: false },
    { key: 'dt_nasc_mov', label: 'Data Nascimento', required: false },
    { key: 'matricula_mov', label: 'Matrícula', required: false },
    { key: 'empresa_mov', label: 'Empresa', required: false },
    { key: 'cd_empresa_mov', label: 'Cód. Empresa', required: false },
    { key: 'id_grupo_mov', label: 'ID Grupo', required: false },
    { key: 'id_faixa_mov', label: 'ID Faixa', required: false },
    { key: 'id_sub_mov', label: 'ID Sub', required: false },
    { key: 'obs_mov', label: 'Observação', required: false },
  ];

  const handleImportData = async (data) => {
    setShowImportModal(false);
    let XLSX;
    try {
        XLSX = await import('xlsx');
    } catch (e) {
        console.error('Erro ao carregar xlsx', e);
        toast.error('Erro ao carregar biblioteca Excel');
        return;
    }

    const toastId = toast.loading('Importando registros...');
    let successCount = 0;
    let errorCount = 0;
    let firstError = null;

    const parseExcelDate = (val) => {
        if (!val) return null;
        if (typeof val === 'number') {
            if (XLSX && XLSX.SSF) {
                const date = XLSX.SSF.parse_date_code(val);
                if (date && date.y && date.m && date.d) {
                    return `${date.y}-${String(date.m).padStart(2,'0')}-${String(date.d).padStart(2,'0')}`;
                }
            }
            return null;
        }
        if (typeof val === 'string') {
                if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                    const [d, m, y] = val.split('/');
                    return `${y}-${m}-${d}`;
                }
                if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        }
        return null;
    };

    try {
        for (const row of data) {
            let idGrupo = row.id_grupo_mov;
            if (idGrupo && isNaN(idGrupo)) {
                const g = (grupos || []).find(x => x.desc_grupo && String(x.desc_grupo).toLowerCase().trim() === String(idGrupo).toLowerCase().trim());
                if (g) idGrupo = g.id_grupo;
                else idGrupo = null; 
            }

            let idFaixa = row.id_faixa_mov;
            if (idFaixa && isNaN(idFaixa)) {
                const f = (faixas || []).find(x => x.desc_faixa && String(x.desc_faixa).toLowerCase().trim() === String(idFaixa).toLowerCase().trim());
                if (f) idFaixa = f.id;
                else idFaixa = null;
            }

            const payload = {
                nome_mov: row.nome_mov,
                cpf_mov: row.cpf_mov,
                dt_nasc_mov: parseExcelDate(row.dt_nasc_mov),
                matricula_mov: row.matricula_mov,
                empresa_mov: row.empresa_mov,
                cd_empresa_mov: row.cd_empresa_mov,
                id_grupo_mov: idGrupo, 
                id_faixa_mov: idFaixa, 
                id_sub_mov: row.id_sub_mov,
                obs_mov: row.obs_mov,
                id_ativo: 1
            };

            // Basic cleanup of empty values
            Object.keys(payload).forEach(key => {
                if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
                    delete payload[key];
                }
            });

            try {
                await api.post('/movimentacao', payload);
                successCount++;
            } catch (err) {
                console.error('Erro importando linha', row, err);
                errorCount++;
                if (!firstError) firstError = err?.response?.data?.message || err.message;
            }
        }
        toast.dismiss(toastId);
        if (errorCount === 0) {
            toast.success(`${successCount} registros importados com sucesso!`);
        } else {
            toast.error(`${successCount} importados. ${errorCount} falhas. Primeiro erro: ${firstError || 'Desconhecido'}`);
        }
        loadMovimentacoes();
    } catch (err) {
        console.error(err);
        toast.dismiss(toastId);
        toast.error('Erro geral: ' + (err.message || String(err)));
    }
  };

  const forceCloseProgress = () => {
    setShowProgressModal(false);
    setProgressState({ percent: 0, step: 0, status: 'idle', count: 0, errorMsg: '' });
    setRequestInfo(null);
    setShowErrorDetails(false);
  };
  
  const [formData, setFormData] = useState({
    nome_mov: '',
    dt_nasc_mov: '',
    cpf_mov: '',
    id_faixa_mov: '',
    id_sub_mov: '',
    id_grupo_mov: '',
    cd_empresa_mov: '',
    empresa_mov: '',
    matricula_mov: '',
    obs_mov: '',
    id_cli: '',
    id_ativo: 1
  });

  
  // Refs para date picker
  const dtNascRef = useRef(null);

  useEffect(() => { 
    loadMovimentacoes(); 
    loadClientes();
    loadGrupos();
    loadFaixas();
  }, []);

  useEffect(() => {
    document.title = 'Movimentação - EstOX';
  }, []);

  const loadMovimentacoes = async () => {
    try {
      const response = await api.get('/movimentacao');
      setMovimentacoes(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar movimentações:', error);
      toast.error('Erro ao carregar movimentações');
    } finally {
      setLoading(false);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/cliente');
      const data = response.data.data || [];
      setClientes(data.sort((a, b) => (a.nome_cli || '').localeCompare(b.nome_cli || '')));
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      // Não exibir erro pro usuário se falhar, apenas logar, pois pode não ser crítico
    }
  };

  const loadGrupos = async () => {
    try {
      const response = await api.get('/gruposegur');
      const data = response.data.data || [];
      setGrupos(data.sort((a, b) => (a.desc_grupo || '').localeCompare(b.desc_grupo || '')));
    } catch (error) {
      console.error('Erro ao carregar grupos segur:', error);
    }
  };

  const loadFaixas = async () => {
    try {
      const response = await api.get('/faixas');
      const data = response.data.data || [];
      setFaixas(data.sort((a, b) => (a.desc_faixa || '').localeCompare(b.desc_faixa || '')));
    } catch (error) {
      console.error('Erro ao carregar faixas:', error);
    }
  };




  const formatDateInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0,8);
    const dd = digits.slice(0,2);
    const mm = digits.slice(2,4);
    const yyyy = digits.slice(4,8);
    let out = '';
    if (dd) out += dd;
    if (mm) out += '/' + mm;
    if (yyyy) out += '/' + yyyy;
    return out;
  };

  const formatCPFInput = (value) => {
    let v = value.replace(/\D/g, "").slice(0, 11);
    if (v.length > 9) return v.replace(/(\d{3})(\d{3})(\d{3})(\d)/, "$1.$2.$3-$4");
    if (v.length > 6) return v.replace(/(\d{3})(\d{3})(\d)/, "$1.$2.$3");
    if (v.length > 3) return v.replace(/(\d{3})(\d)/, "$1.$2");
    return v;
  };

  const formatDateToISO = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    const el = progressRef.current;
    if (!el) return;
    if (progressState.status !== 'running') return;
    el.animate([
      { filter: 'brightness(1)' },
      { filter: 'brightness(1.25)' },
      { filter: 'brightness(1)' }
    ], { duration: 600, easing: 'ease-in-out' });
  }, [progressState.percent, progressState.status]);

  const handleConsolidar = async (e) => {
    e.preventDefault();
    const nome = String(consolidarForm.nome || '').trim();
    const dtCad = consolidarForm.data || new Date().toISOString().slice(0,10);
    const mesano = String(consolidarForm.mesano || '').trim();
    if (!nome) { toast.error('Informe o nome da consolidação'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dtCad)) { toast.error('Data inválida'); return; }
    if (!/^\d{2}\/\d{4}$/.test(mesano)) { toast.error('Mês/Ano deve ser MM/AAAA'); return; }
    const ids = sortedMovs.filter(m => m.id_ativo === 1).map(m => m.id_mov);
    if (!ids.length) { toast.error('Nenhum registro ativo filtrado para consolidar'); return; }
    setShowConsolidarModal(false);
    setShowProgressModal(true);
    setShowErrorDetails(false);
    setProgressState({ percent: 5, step: 0, status: 'running', count: 0 });
    try {
      await wait(300);
      setProgressState((p) => ({ ...p, percent: 25, step: 1 }));
      await wait(300);
      setProgressState((p) => ({ ...p, percent: 45, step: 2 }));
      let response;
      
      // Sending only IDs to reduce payload size. Backend will fetch records.
      const payload = { dt_cad: dtCad, mesano, nome, ids };
      
      setRequestInfo({ endpoint: '/consolidacao/run', payload });
      try {
        response = await api.post('/consolidacao/run', payload);
      } catch (err) {
        if (err?.response?.status === 404) {
          // Fallback handling if needed, though runConsolidacao should exist
          const fallbackPayload = { ...payload, nome_cons: nome };
          setRequestInfo({ endpoint: '/consolidacao', payload: fallbackPayload });
          response = await api.post('/consolidacao', fallbackPayload);
        } else { throw err; }
      }
      const { data } = response;
      setRequestInfo((prev) => ({ ...(prev || {}), status: 200, response: data }));
      setProgressState({ percent: 100, step: 3, status: 'success', count: data?.count || 0, errorMsg: '' });
      toast.success(`Consolidação concluída: ${data?.count || 0} registros`);
      await wait(600);
      setShowProgressModal(false);
      setShowErrorDetails(false);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao consolidar';
      setProgressState((p) => ({ ...p, status: 'error', errorMsg: msg }));
      setRequestInfo((prev) => ({ ...(prev || {}), status: error?.response?.status, response: error?.response?.data }));
      toast.error(msg);
    }
  };
  



  const exportToExcel = async () => {
    if (!sortedMovs.length) {
      toast.error('Nenhum registro para exportar');
      return;
    }

    let XLSX;
    try {
        XLSX = await import('xlsx');
    } catch (e) {
        console.error('Erro ao carregar xlsx', e);
        toast.error('Erro ao carregar biblioteca Excel');
        return;
    }

    const dataToExport = sortedMovs.map(mov => ({
      ID: mov.id_mov,
      Nome: mov.nome_mov || '-',
      CPF: formatCPF(mov.cpf_mov),
      Empresa: getEmpresaNome(mov),
      'Data Nasc.': formatDateDisplay(mov.dt_nasc_mov),
      Matrícula: mov.matricula_mov || '-',
      Grupo: getGrupoDesc(mov),
      Faixa: mov.id_faixa_mov ? ((faixas.find(f => f.id === mov.id_faixa_mov) || {}).desc_faixa || mov.id_faixa_mov) : '-',
      Sub: mov.id_sub_mov ?? '-',
      'CDL/ACE': mov.id_cdl_ace || '-',
      Obs: mov.obs_mov || '-',
      Status: mov.id_ativo === 1 ? 'Ativo' : 'Inativo'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Movimentações");
    
    // Auto-width for columns
    const wscols = Object.keys(dataToExport[0]).map(k => ({ wch: Math.max(k.length, 15) }));
    ws['!cols'] = wscols;

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Movimentacoes_${dataAtual}.xlsx`);
    toast.success('Arquivo Excel gerado com sucesso!');
  };

  const openModal = (mov = null) => {
    setEditingMov(mov);
    const cliResolved = resolveClienteFromMov(mov);
    setFormData({
      nome_mov: mov?.nome_mov || '',
      dt_nasc_mov: mov?.dt_nasc_mov ? formatDateDisplay(mov.dt_nasc_mov) : '',
      cpf_mov: mov?.cpf_mov || '',
      id_faixa_mov: mov?.id_faixa_mov || '',
      id_sub_mov: mov?.id_sub_mov || '',
      id_grupo_mov: mov?.id_grupo_mov || '',
      cd_empresa_mov: cliResolved?.cd_supera_cli || mov?.cd_empresa_mov || '',
      empresa_mov: mov?.empresa_mov || '',
      matricula_mov: mov?.matricula_mov || '',
      obs_mov: mov?.obs_mov || '',
      id_cli: cliResolved?.id_cli || mov?.id_cli || '',
      id_ativo: mov?.id_ativo !== undefined ? parseInt(mov.id_ativo) : 1
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMov(null);
    setFormData({
      nome_mov: '',
      dt_nasc_mov: '',
      cpf_mov: '',
      id_faixa_mov: '',
      id_sub_mov: '',
      id_grupo_mov: '',
      cd_empresa_mov: '',
      empresa_mov: '',
      matricula_mov: '',
      obs_mov: '',
      id_cli: '',
      id_ativo: 1
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    const requiredFields = [
      { key: 'nome_mov', label: 'Nome' },
      { key: 'id_ativo', label: 'Status' },
      { key: 'dt_nasc_mov', label: 'Data de Nascimento' },
      { key: 'cpf_mov', label: 'CPF' },
      { key: 'id_cli', label: 'Empresa' },
      { key: 'cd_empresa_mov', label: 'Cód. Empresa' },
      { key: 'id_faixa_mov', label: 'ID Faixa' },
      { key: 'id_sub_mov', label: 'Sub' },
      { key: 'id_grupo_mov', label: 'ID Grupo' }
    ];

    for (const field of requiredFields) {
      const val = formData[field.key];
      if (val === undefined || val === null || String(val).trim() === '') {
        toast.error(`${field.label} é obrigatório`);
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        dt_nasc_mov: formatDateToISO(formData.dt_nasc_mov)
      };

      if (editingMov) {
        await api.put(`/movimentacao/${editingMov.id_mov}`, payload);
        toast.success('Movimentação atualizada com sucesso!');
      } else {
        await api.post('/movimentacao', payload);
        toast.success('Movimentação criada com sucesso!');
      }
      closeModal();
      loadMovimentacoes();
    } catch (error) {
      console.error('Erro ao salvar movimentação:', error);
      const msg = error?.response?.data?.message || 'Erro ao salvar movimentação';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingMov) return;
    setIsDeleting(true);
    try {
      await api.delete(`/movimentacao/${deletingMov.id_mov}`);
      toast.success('Movimentação excluída com sucesso!');
      setShowDeleteModal(false);
      setDeletingMov(null);
      loadMovimentacoes();
    } catch (error) {
      console.error('Erro ao excluir movimentação:', error);
      const msg = error?.response?.data?.message || 'Erro ao excluir movimentação';
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const TableHeader = () => {
    const renderSortIcon = (key) => {
      if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400 opacity-50 group-hover:opacity-100" />;
      return sortConfig.direction === 'asc' 
         ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
         : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
    };

    const headers = [
      { key: 'id', label: 'ID', className: 'w-16' },
      { key: 'nome', label: 'Nome', className: 'max-w-[200px]' },
      { key: 'cpf', label: 'CPF', className: '' },
      { key: 'nasc', label: 'NASC.', className: '' },
      { key: 'empresa', label: 'Empresa', className: 'min-w-[260px] max-w-[380px]' },
      { key: 'faixa', label: 'Faixa', className: '' },
      { key: 'sub', label: 'Sub', className: '' },
      { key: 'status', label: 'Status', className: '' },
    ];

    return (
      <thead className="bg-gray-50 sticky top-0 z-10">
        <tr>
          {headers.map(h => (
            <th 
              key={h.key}
              onClick={() => handleSort(h.key)}
              className={`px-2 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors select-none ${h.className}`}
            >
              <div className="flex items-center">
                {h.label}
                {renderSortIcon(h.key)}
              </div>
            </th>
          ))}
          <th className="px-2 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Ações</th>
        </tr>
      </thead>
    );
  };

  const DataRow = ({ mov }) => (
    <tr key={mov.id_mov} className="hover:bg-gray-50 bg-white">
      <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">{mov.id_mov}</td>
      <td className="px-2 py-2 text-sm text-gray-900 truncate max-w-[200px]" title={mov.nome_mov || ''}>{mov.nome_mov || '-'}</td>
      <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">{formatCPF(mov.cpf_mov)}</td>
      <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDateDisplay(mov.dt_nasc_mov)}</td>
      <td className="px-2 py-2 text-sm text-gray-900 truncate min-w-[260px] max-w-[380px]" title={getEmpresaNome(mov)}>{getEmpresaNome(mov)}</td>
      <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">{(faixas.find(f => f.id === mov.id_faixa_mov) || {}).desc_faixa || mov.id_faixa_mov || '-'}</td>
      <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">{mov.id_sub_mov ?? '-'}</td>
      <td className="px-2 py-2 text-sm whitespace-nowrap">
        <span className={`px-2 py-1 inline-flex text-sm leading-4 font-semibold rounded-full ${mov.id_ativo === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mov.id_ativo === 1 ? 'Ativo' : 'Inativo'}
        </span>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2 justify-center">
          <IconButton icon={Edit3} onClick={() => openModal(mov)} variant="warning" size="sm" title="Editar" />
          <IconButton icon={Trash2} onClick={() => { setDeletingMov(mov); setShowDeleteModal(true); }} variant="danger" size="sm" title="Excluir" />
        </div>
      </td>
    </tr>
  );

  const GroupBlock = ({ item, level = 0, parentKey = '' }) => {
    const uniqueKey = parentKey ? `${parentKey}-${item.groupValue}` : item.groupValue;
    // Se não estiver no conjunto de expandidos, adicionamos por padrão se for o primeiro nível (opcional, mas comum em master-detail)
    // Mas vamos manter a lógica de toggle manual para consistência.
    const isExpanded = expandedGroups.has(uniqueKey);

    return (
      <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
        {/* Cabeçalho do Grupo */}
        <div 
          className="bg-gray-100 p-4 flex items-center justify-between cursor-pointer hover:bg-gray-200 transition-colors border-b border-gray-200"
          onClick={() => toggleGroup(uniqueKey)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown size={24} className="text-gray-600" /> : <ChevronRight size={24} className="text-gray-600" />}
            <span className="font-bold text-gray-700 uppercase text-lg">
              {labelForField(item.groupKey)}: {item.groupValue}
            </span>
            <span className="bg-blue-100 text-blue-800 text-base px-3 py-0.5 rounded-full font-medium">
              {item.count} itens
            </span>
          </div>
        </div>

        {/* Conteúdo do Grupo */}
        {isExpanded && (
          <div className="p-0">
            {item.children && item.children.length > 0 && item.children[0].isGroup ? (
              // Se houver subgrupos, renderiza recursivamente com indentação visual
              <div className="p-4 bg-gray-50 flex flex-col gap-4">
                {item.children.map(child => (
                  <GroupBlock 
                    key={`${uniqueKey}-${child.groupValue}`} 
                    item={child} 
                    level={level + 1} 
                    parentKey={uniqueKey} 
                  />
                ))}
              </div>
            ) : (
              // Se forem folhas (dados finais)
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 table-auto">
                    <TableHeader />
                    <tbody className="bg-white divide-y divide-gray-200">
                      {item.children.map(mov => (
                        <DataRow key={mov.id_mov} mov={mov} />
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Rodapé de Totais do Grupo */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-end items-center gap-4 text-base font-bold text-gray-700">
                  <span>Subtotal (Contagem): {item.count} registros</span>
                  {/* Se houver colunas de valor no futuro, adicione aqui */}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Movimentação</h1>
        <p className="text-gray-600">Gerencie as movimentações</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Activity className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{movimentacoes.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Search className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtradas</p>
              <p className="text-2xl font-bold text-gray-900">{sortedMovs.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Plus className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ações</p>
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Nova Movimentação" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar... (Ex: N:Maria C:123)"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-1">
                Filtros Inteligentes: <b>N:</b>Nome, <b>C:</b>CPF, <b>E:</b>Empresa. (Ex: <i>N:Lucas C:044</i>)
              </p>
            </div>
            <IconButton
              icon={Filter}
              onClick={() => setShowOnlyActive(v => !v)}
              variant={showOnlyActive ? 'success' : 'secondary'}
              title={showOnlyActive ? 'Mostrando somente ativos (clique para ver todos)' : 'Mostrar somente ativos'}
            />
            <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Nova Movimentação" />
            <IconButton icon={Archive} onClick={() => setShowConsolidarModal(true)} variant="purple" title="Consolidar" />
            <IconButton icon={ShieldCheck} onClick={() => setShowValidationModal(true)} variant="info" title="Validar Importação" />
            <IconButton icon={FileSpreadsheet} onClick={exportToExcel} variant="success" title="Exportar Excel" />
            <IconButton icon={Upload} onClick={() => setShowImportModal(true)} variant="success" title="Importar" />
          </div>
        </div>
      </div>

      <GroupPanel
        availableColumns={availableColumns}
        activeGroups={breakFields}
        onAddGroup={handleAddGroup}
        onRemoveGroup={handleRemoveGroup}
        onReorderGroup={handleReorderGroup}
        onClearGroups={handleClearGroups}
      />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {breakFields.length > 0 ? (
          // Visualização Agrupada (Lista de Blocos)
          <div className="p-4 bg-gray-50 max-h-[calc(100vh-300px)] overflow-y-auto">
             {groupedData.map((group) => (
                <GroupBlock key={group.groupValue} item={group} />
             ))}
          </div>
        ) : (
          // Visualização Plana (Tabela Normal)
          <div className="overflow-x-scroll overflow-y-auto max-h-[calc(100vh-300px)]">
            <table className="min-w-full divide-y divide-gray-200 table-auto">
              <TableHeader />
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedMovs.map(mov => (
                  <DataRow key={mov.id_mov} mov={mov} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {sortedMovs.length === 0 && (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma movimentação encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">{searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando uma nova movimentação.'}</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{editingMov ? 'Editar Movimentação' : 'Nova Movimentação'}</h3>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-9">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input 
                      type="text" 
                      required 
                      disabled={formData.id_ativo === 0}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${formData.id_ativo === 0 ? 'bg-gray-100 text-gray-500' : ''}`}
                      value={formData.nome_mov} 
                      onChange={(e) => setFormData({ ...formData, nome_mov: e.target.value.toUpperCase() })} 
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={formData.id_ativo}
                      onChange={(e) => setFormData({ ...formData, id_ativo: parseInt(e.target.value) })}
                    >
                      <option value={1}>Ativo</option>
                      <option value={0}>Inativo</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento *</label>
                    <div className="relative">
                        <input
                            required
                            type="text"
                            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.dt_nasc_mov}
                            onChange={(e) => setFormData({ ...formData, dt_nasc_mov: formatDateInput(e.target.value) })}
                            placeholder="dd/mm/aaaa"
                        />
                         <button
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-500"
                            onClick={() => {
                                if (dtNascRef.current?.showPicker) dtNascRef.current.showPicker();
                                else dtNascRef.current?.focus();
                            }}
                        >
                            <Calendar className="h-5 w-5" />
                        </button>
                        <input
                            type="date"
                            ref={dtNascRef}
                            className="absolute opacity-0 pointer-events-none bottom-0 left-0"
                            onChange={(e) => setFormData({ ...formData, dt_nasc_mov: isoToDisplay(e.target.value) })}
                        />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                    <input
                      required
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={formData.cpf_mov}
                      onChange={(e) => setFormData({ ...formData, cpf_mov: formatCPFInput(e.target.value) })}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa *</label>
                  <SearchableSelect
                    options={clientes.map((cli) => ({ value: cli.id_cli, label: cli.nome_cli }))}
                    value={formData.id_cli}
                    onChange={(e) => {
                      const newIdCli = e.target.value;
                      const cli = clientes.find(c => String(c.id_cli) === String(newIdCli));
                      setFormData({
                        ...formData,
                        id_cli: newIdCli,
                        cd_empresa_mov: cli?.cd_supera_cli || ''
                      });
                    }}
                    placeholder="Selecione uma empresa"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cód. Empresa *</label>
                    {(() => {
                      const selectedCli = clientes.find(c => String(c.id_cli) === String(formData.id_cli));
                      const optionVal = selectedCli?.cd_supera_cli || '';
                      return (
                        <select
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={formData.cd_empresa_mov}
                          onChange={(e) => setFormData({ ...formData, cd_empresa_mov: e.target.value })}
                          disabled={!formData.id_cli}
                        >
                          {!formData.id_cli && (
                            <option value="">Selecione uma empresa primeiro</option>
                          )}
                          {formData.id_cli && (
                            optionVal ? (
                              <option value={optionVal}>{optionVal}</option>
                            ) : (
                              <option value="">Sem código disponível</option>
                            )
                          )}
                        </select>
                      );
                    })()}
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ID Faixa *</label>
                        <SearchableSelect
                          options={faixas.map((f) => ({ value: f.id, label: f.desc_faixa }))}
                          value={formData.id_faixa_mov}
                          onChange={(e) => setFormData({ ...formData, id_faixa_mov: e.target.value })}
                          placeholder="Selecione a faixa"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sub *</label>
                        <input
                          required
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={formData.id_sub_mov}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, '');
                            setFormData({ ...formData, id_sub_mov: v });
                          }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ID Grupo *</label>
                        <SearchableSelect
                          options={grupos.map((g) => ({ value: g.id_grupo, label: g.desc_grupo }))}
                          value={formData.id_grupo_mov}
                          onChange={(e) => setFormData({ ...formData, id_grupo_mov: e.target.value })}
                          placeholder="Selecione o grupo"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Matrícula</label>
                    <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.matricula_mov} onChange={(e) => setFormData({ ...formData, matricula_mov: e.target.value })} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    value={formData.obs_mov}
                    onChange={(e) => setFormData({ ...formData, obs_mov: e.target.value })}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                  <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : (editingMov ? 'Atualizar' : 'Criar')} />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showConsolidarModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-lg bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Consolidar Movimentações</h3>
              <form onSubmit={handleConsolidar} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={consolidarForm.data}
                    onChange={(e) => setConsolidarForm({ ...consolidarForm, data: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Consolidação</label>
                  <input
                    type="text"
                    maxLength={160}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={consolidarForm.nome}
                    onChange={(e) => setConsolidarForm({ ...consolidarForm, nome: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mês/Ano</label>
                  <input
                    type="text"
                    maxLength={7}
                    placeholder="MM/AAAA ex.: 05/2025"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={consolidarForm.mesano}
                    onChange={(e) => setConsolidarForm({ ...consolidarForm, mesano: e.target.value })}
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <IconButton icon={X} onClick={() => setShowConsolidarModal(false)} variant="secondary" title="Cancelar" />
                  <IconButton icon={Archive} type="submit" variant="purple" title="Consolidar" />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportData}
        fields={importFields}
        title="Importar Movimentações"
      />

      <MovimentacaoValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        clientes={clientes}
        faixas={faixas}
        onCompleted={loadMovimentacoes}
      />

      {showProgressModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50">
          <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Archive className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Consolidação em andamento</h3>
            </div>
            <div className="flex justify-between text-xs mb-3">
              {['Validação','Preparação','Consolidação','Concluído'].map((label, idx) => (
                <div key={label} className={`flex-1 text-center ${progressState.step >= idx ? 'text-purple-700' : 'text-gray-400'}`}>{label}</div>
              ))}
            </div>
            <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden">
              <div ref={progressRef} style={{ width: `${progressState.percent}%` }} className="h-full bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 transition-all duration-300"></div>
            </div>
          <div className="mt-3 flex justify-between text-sm text-gray-700">
            <span>{progressState.percent}%</span>
            {progressState.status === 'success' && <span>{progressState.count} registros</span>}
            {progressState.status === 'error' && <span className="text-red-600">Erro</span>}
          </div>
          {progressState.status === 'error' && progressState.errorMsg && (
            <div className="mt-2 text-xs text-red-700">{progressState.errorMsg}</div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <IconButton icon={X} onClick={forceCloseProgress} variant="secondary" title="Fechar" />
            {progressState.status === 'error' && (
              <IconButton icon={Info} onClick={() => setShowErrorDetails((v) => !v)} variant="info" title="Detalhe" />
            )}
          </div>
          {progressState.status === 'error' && showErrorDetails && requestInfo && (
            <div className="mt-3 text-xs text-gray-700">
              <div className="font-medium">Detalhes da requisição</div>
              <div className="mt-1">Endpoint: {requestInfo.endpoint}</div>
              <div className="mt-1">Status: {requestInfo.status || '-'}</div>
              <div className="mt-1">Payload:</div>
              <pre className="bg-gray-100 p-2 rounded overflow-x-auto">{JSON.stringify(requestInfo.payload, null, 2)}</pre>
              {requestInfo.response && (
                <>
                  <div className="mt-1">Resposta:</div>
                  <pre className="bg-gray-100 p-2 rounded overflow-x-auto">{JSON.stringify(requestInfo.response, null, 2)}</pre>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Confirmar Exclusão</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">Tem certeza que deseja excluir a movimentação &quot;{deletingMov?.nome_mov}&quot;? Esta ação não pode ser desfeita.</p>
              </div>
              <div className="flex justify-center space-x-3 px-4 py-3">
                <IconButton icon={X} onClick={() => { setShowDeleteModal(false); setDeletingMov(null); }} variant="secondary" title="Cancelar" />
                <IconButton icon={Trash2} onClick={handleDelete} disabled={isDeleting} variant="danger" title={isDeleting ? 'Excluindo...' : 'Excluir'} />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Movimentacao;
